import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Link,
  Menu,  
  MenuItem,  
  Table, 
  TableBody,
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Tooltip,
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  DeleteTenantBIZidentity,
  GetTenantsBIZidentity 
} from '../../../../redux/actions/business/identity/tenantsActions';

import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import TenantsModal from './TenantsModal';

import global from "../../../../global.scss";

const TenantsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [tenantsModalOpen, setTenantsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [tenantsList, setTenantsList] = useState([]);
  const [tenantsTotal, setTenantsTotal] = useState(0);

  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [tenantMenuAnchor, setTenantMenuAnchor] = useState(null);

  const [deleteId, setDeleteId] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Name | Legal name",
      sort: "name",
    },
    {
      label: "About",
      sort: "about",
    },
    {
      label: "TaxId",
      sort: "tax_id",
    },
    {
      label: "Address | City - State",
      sort: "address",
    },
    {
      label: "Country",
      sort: "country",
    },
    {
      label: "Logo url",
      sort: "logo_url",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatTenantsList(state.tenantsList.records || []);
      setTenantsTotal(state.tenantsList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatTenantsList = (list) => {
    setTenantsList(list);
  };

  async function handleGetTenants (){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    dispatch(GetTenantsBIZidentity(page+1, rowsPerPage, query, setLoading));
  }

  const handleDelete = () => {
    setDeleteConfirmationModalOpen(false);
    dispatch(DeleteTenantBIZidentity(deleteId, setDeleting)).then(() => {
      handleGetTenants().then(() => {
        setDeleteId(null);
      })
    });
  };

  return (
    <Box>

      <TenantsModal 
        open={tenantsModalOpen}
        setOpen={setTenantsModalOpen}
        title={modalTitle}
        update={update}
        page={page}
        rowsPerPage={rowsPerPage}
        />

      <ConfirmationModal 
        open={deleteConfirmationModalOpen}
        setOpen={setDeleteConfirmationModalOpen}
        title={"Delete " + modalTitle}
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
        />

      <TopBar
        title={"Tenants"}
        search={search}
        searchPlaceholder={"Search id, name, tax id or address"}
        onSearchChange={handleSearchChange}
        button1Text={"Create Tenant"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Create Tenant");
          setTenantsModalOpen(!tenantsModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          tenantsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No tenant found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                tenantsList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.name || <NA />}<br/>
                      {row.legal_name || <NA />}
                    </TableCell>
                    <TableCell>{row.about || <NA />}</TableCell>
                    <TableCell>{row.tax_id || <NA />}</TableCell>
                    <TableCell>
                      {row.address || <NA />},<br/>
                      {row.city || <NA />}&nbsp;-&nbsp;{row.state},<br/>
                      {row.pincode}<br/>
                    </TableCell>
                    <TableCell>{row.country || <NA />}</TableCell>
                    <TableCell>
                      {
                        row.logo_url
                        ?
                        <Tooltip title={row.logo_url}>
                          <Link
                            href={row.logo_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                            sx={{
                              color: global['secondary_light'],
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              maxWidth: "220px",
                            }}>
                            <Box component="span" className="longText">
                              {row.logo_url}
                            </Box>
                            <OpenInNewIcon sx={{ fontSize: "14px", flexShrink: 0 }} />
                          </Link>
                        </Tooltip>
                        :
                        <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                    <TableCell>
                    {
                      row.id === deleteId && deleting
                      ?
                      <CircularProgress size={26} sx={{ margin: "7px" }}/>
                      :
                      <IconButton
                        onClick={(e) => {
                          setTenantMenuAnchor(e.currentTarget);
                          setTenantMenuOpen(true);
                          setDeleteId(row.id);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    }
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={tenantsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={tenantMenuOpen}
        anchorEl={tenantMenuAnchor}
        onClose={() => setTenantMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setTenantMenuOpen(false);
            setTenantsModalOpen(!tenantsModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setTenantMenuOpen(false);
            setDeleteConfirmationModalOpen(true);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <DeleteOutlineOutlinedIcon />
            Delete
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default TenantsPage; 